import type { CanonicalEntity } from '@/shared/api/types';
import { FurnitureMotionObject } from './furnitureMotion';
import {
  HARDWARE_OBJECT_KINDS,
  MODULE_OBJECT_KINDS,
  PANEL_LIKE_OBJECT_KINDS,
  VISUAL_OBJECT_KIND,
} from './entityKinds';

export type FurnitureInspectorKind = 'panel' | 'hardware' | 'service' | 'visual';

/**
 * UI-facing wrapper over one canonical scene entity.
 *
 * Menus and panels should not reimplement role-string rules.  They receive an
 * entity and ask this object what product actions make sense for it.
 */
export class FurnitureSceneEntity {
  readonly entity: CanonicalEntity;

  constructor(entity: CanonicalEntity) {
    this.entity = entity;
  }

  get id(): string {
    return this.entity.id;
  }

  get kind(): string {
    return this.objectKind();
  }

  private get domain(): Record<string, unknown> {
    const domain = this.entity.domain;
    return domain && typeof domain === 'object' ? domain as Record<string, unknown> : {};
  }

  private objectKind(): string {
    const value = this.domain.objectKind;
    return typeof value === 'string' && value.trim() ? value.trim().toLowerCase() : '';
  }

  isModule(): boolean {
    return MODULE_OBJECT_KINDS.has(this.objectKind());
  }

  isComposition(): boolean {
    return this.objectKind() === 'composition';
  }

  /**
   * Щит ИЛИ декоративная вставка.  `isPanelLikeEntity` (`entityDomain.ts`)
   * отвечает на тот же вопрос без `visual` — расхождение сохранено как было,
   * словарь у обоих теперь один.
   */
  isPart(): boolean {
    return PANEL_LIKE_OBJECT_KINDS.has(this.objectKind()) || this.isVisual();
  }

  isHardware(): boolean {
    return HARDWARE_OBJECT_KINDS.has(this.objectKind());
  }

  isServiceHelper(): boolean {
    const contract = this.domain.visibilityContract as Record<string, unknown> | undefined;
    const objectKind = String(this.domain.objectKind || '').toLowerCase();
    const inspectorKind = String(contract?.inspectorKind || '').toLowerCase();
    return objectKind === 'service'
      || objectKind === 'helper'
      || objectKind === 'module_niche'
      || inspectorKind === 'service'
      || inspectorKind === 'helper';
  }

  isVisual(): boolean {
    return this.objectKind() === VISUAL_OBJECT_KIND;
  }

  isFront(): boolean {
    if (!this.isPart()) return false;
    const objectKind = this.objectKind();
    if (objectKind === 'facade' || objectKind === 'drawer_front') return true;
    const owner = String((this.domain.motionContract as Record<string, unknown> | undefined)?.owner || '').toLowerCase();
    return owner === 'facade' || owner === 'drawer_front';
  }

  canSwapFacadeMaterial(): boolean {
    return this.isModule() || this.isComposition() || this.isFront();
  }

  canAnimate(): boolean {
    return new FurnitureMotionObject(this.entity).canAnimate();
  }

  inspectorKind(): FurnitureInspectorKind {
    if (this.isServiceHelper()) return 'service';
    if (this.isHardware()) return 'hardware';
    if (this.isVisual()) return 'visual';
    return 'panel';
  }

  isHardwareLike(): boolean {
    return this.isHardware();
  }

  isModuleRenderChild(): boolean {
    return this.isPart() || this.isHardware();
  }

  canReceiveSparseMesh(): boolean {
    return !this.isServiceHelper();
  }

  moduleOwnerId(): string {
    return String(this.entity.moduleId || this.domain.moduleId || '');
  }
}
